import { useCallback, useMemo } from 'react';
import { sortNotificationsByCreatedAt } from '@connect-x-app/shared-utils';
import { Notifications } from '@connect-x-app/shared-view-models';

import { useNotificationStore } from '../../stores';
import { useGetPaginatedNotifications } from '../queries';
import { useNotificationSocketSendEvents } from '../socketEvents';

export const useNotifications = (): Notifications => {
  const {
    markNotificationAsDismissed,
    notifications: notificationsMap,
    setNotifications,
    unreadNotificationsCount,
  } = useNotificationStore();
  const { dismissNotification: sendDismissNotification } = useNotificationSocketSendEvents();

  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isPending, isRefetching, refetch } =
    useGetPaginatedNotifications();

  const dismissNotification = useCallback(
    async (notificationId: string) => {
      const ack = await sendDismissNotification(notificationId);
      if (ack?.ok) {
        markNotificationAsDismissed(notificationId);
      }
    },
    [markNotificationAsDismissed, sendDismissNotification],
  );

  const notifications = useMemo(() => {
    return Array.from(notificationsMap.values()).sort(sortNotificationsByCreatedAt);
  }, [notificationsMap]);

  const fetchOlderNotifications = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const refetchNotifications = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data: data?.pages,
    dismissNotification,
    error,
    fetchOlderNotifications,
    hasOlderNotifications: hasNextPage,
    isFetchingNotifications: isFetching,
    isFetchingOlderNotifications: isFetchingNextPage,
    isPending,
    isRefetchingNotifications: isRefetching,
    notifications,
    refetchNotifications,
    setNotifications,
    unreadNotificationsCount,
  };
};
